import { Badge } from "@/components/ui/badge";
import { formatEnumLabel } from "@/lib/formatters";
import type { EnumBadgeSemanticTone } from "@/types";
import { cn } from "@/utils";

export type { EnumBadgeSemanticTone };

const SEMANTIC_TONE: Record<
  EnumBadgeSemanticTone,
  { variant: "outline" | "destructive" | "secondary"; className: string }
> = {
  success: {
    variant: "outline",
    className: "border-green-200 bg-green-100 text-green-800 hover:bg-green-100",
  },
  failure: {
    variant: "destructive",
    className: "",
  },
  warning: {
    variant: "outline",
    className: "border-amber-200 bg-amber-100 text-amber-900 hover:bg-amber-100",
  },
  info: {
    variant: "outline",
    className: "border-blue-200 bg-blue-100 text-blue-800 hover:bg-blue-100",
  },
  neutral: {
    variant: "outline",
    className: "",
  },
  muted: {
    variant: "secondary",
    className: "",
  },
};

/**
 * Display a call result badge (Success / Failed / Active).
 */
export function ResultBadge({ success }: { success: string }) {
  if (success === "true")
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Success</Badge>;
  if (success === "false")
    return <Badge variant="destructive">Failed</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Active</Badge>;
}

/**
 * Display a case status badge (Completed / Failed / In Progress / Pending / etc.).
 */
export function StatusBadge({ status, success }: { status?: string; success?: string }) {
  if (success === "true")
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>;
  if (success === "false")
    return <Badge variant="destructive">Failed</Badge>;
  if (status === "IN_PROGRESS")
    return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Progress</Badge>;
  if (status === "PENDING_VERIFICATION")
    return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending Verification</Badge>;
  if (status === "PENDING")
    return <Badge variant="secondary">Pending</Badge>;
  return <Badge variant="outline">{status || "Unknown"}</Badge>;
}

/**
 * Display an enum value as a badge (e.g. pa_status, fail_reason).
 * Automatically replaces underscores with spaces and capitalizes.
 */
export function EnumBadge({
  value,
  variant = "outline",
  semanticTone,
  className,
}: {
  value: string;
  variant?: "outline" | "destructive" | "secondary" | "default";
  semanticTone?: EnumBadgeSemanticTone;
  className?: string;
}) {
  const preset = semanticTone ? SEMANTIC_TONE[semanticTone] : null;
  const resolvedVariant = (preset?.variant ?? variant) as
    | "outline"
    | "destructive"
    | "secondary"
    | "default";
  return (
    <Badge variant={resolvedVariant} className={cn("capitalize", preset?.className, className)}>
      {formatEnumLabel(value)}
    </Badge>
  );
}
